import {
  cacheDecorator,
  networkFirstAndCacheDecorator,
  updateCache,
} from '@ds/swymkit/tools'

import { swymFetch } from '~/services/fetch-swym'

import type { IUpdateParams } from '~/features-shared/content/api/interfaces/params'
import { contentParser } from '~/features-shared/content/api/parser-contents'
import type { IContent } from '~/features-shared/content/interfaces/content'
import { IContentModel } from '~/features-shared/content/interfaces/content-model'
import type { ISwymContent } from '~/features-shared/content/interfaces/swym-content'
import type { TagActions } from '~/features-shared/content-derived-type/utils/derived-types'
import { tagsForV2APIs } from '~/features-shared/content-derived-type/utils/derived-types'
import type { SUBJECT_6W_URI } from '~/features-shared/libs/interfaces/entity'

import type { ICRUDService, ICRUDServiceParams } from '~/interfaces/crud-service'
import type { SwymResponse } from '~/interfaces/services-dto'

interface ICreationPostParams extends Omit<ICRUDServiceParams, 'subject_uri'> {
  title?: string
  community_subject_uri?: SUBJECT_6W_URI
  derivedTypeId?: string
  message?: string
  taggingActions?: TagActions
  published?: boolean
}

type IReadPostParams = ICRUDServiceParams

type IUpdatePostParams = ICRUDServiceParams & Omit<ICreationPostParams, 'published'> & IUpdateParams

type IDeletePostParams = ICRUDServiceParams

export const POST_CRUD_API: ICRUDService<
  IContent,
  ICreationPostParams,
  IReadPostParams,
  IUpdatePostParams,
  IDeletePostParams
> = {
  create(params, tracker) {
    return swymFetch
      .post('/api/v2/posts', {
        json: {
          container_id: params.community_subject_uri,
          derived_type_id: params.derivedTypeId,
          title: params.title,
          body: params.message,
          publication_state: params.published ? 'PUBLISHED' : 'DRAFT',
          tags6w_actions: params.taggingActions ? tagsForV2APIs(params.taggingActions) : undefined,
        },
        tracker,
      })
      .json<SwymResponse<ISwymContent>>()
      .then(response => contentParser(response, true))
      .then(response => updateCache(`post-${response.result.subject_uri}`, response))
  },

  read(params, tracker) {
    return cacheDecorator(`post-${params.subject_uri}`, () => swymFetch
      .get(`/api/v2/posts/${params.subject_uri}`, { tracker })
      .json<SwymResponse<ISwymContent>>()
      .then(response => contentParser(response, true))
      .then((response) => {
        if (response.result.derived_type?.id)
          response.result.model = IContentModel.DerivedPost
        response.result.model = IContentModel.Post
        return response
      }))
  },

  update(params, tracker) {
    return networkFirstAndCacheDecorator(`post-${params.subject_uri}`, () => swymFetch
      .put(`/api/v2/posts/${params.subject_uri}`, {
        json: {
          title: params.title,
          body: params.description,
          publication_state: params.published ? 'PUBLISHED' : 'DRAFT',
          tags6w_actions: params.taggingActions ? tagsForV2APIs(params.taggingActions) : undefined,
        },
        tracker,
      })
      .json<SwymResponse<ISwymContent>>()
      .then(response => contentParser(response, true))
      .then((response) => {
        response.result.model = IContentModel.Post
        return response
      }))
  },

  delete(params, tracker) {
    return swymFetch
      .delete(`/api/v2/posts/${params.subject_uri}`, { tracker })
      .json<SwymResponse<void>>()
  },
}
